import type {Router, RouteLocation} from 'src/types/router';
import type {State as ReduxState} from 'src/reducers';
import type {AtsAnalyticsField, EntityFilter} from 'src/types/ats-settings';
import type {UpdateOptions, AnalyticsQuery} from 'src/utils/analytics-query';
// @ts-expect-error - TS2614 - Module '"src/api-parsers/events"' has no exported member 'ClientEventType'. Did you mean to use 'import ClientEventType from "src/api-parsers/events"' instead?
import type {ClientEventType} from 'src/api-parsers/events';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {connect, useDispatch} from 'react-redux';

import get from 'lodash/get';
import flow from 'lodash/flow';
import isEqual from 'lodash/isEqual';

import {formatPlural} from 'src/utils/intl';
import classify from 'src/utils/classify';

import {getAtsFieldValues} from 'src/selectors/ats-settings';
import {
  selectEntityFilters,
  selectEntityFiltersForType,
  getCandidateFieldName,
} from 'src/selectors/ats-entities';
import {getCanEditSettings} from 'src/selectors/accounts';
import {getTrackingEvents} from 'src/selectors/report';
import {saveDefaultAnalyticsQuery} from 'src/action-creators/report';

import withAnalyticsQuery from 'src/components/analytics/with-query';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import {FilterButton} from 'src/components/lib/analytics-filter-button';
import {
  MessageTypeCard,
  RelatedToCard,
  RecipientCard,
  TrackingEventCard,
  MoreFiltersCard,
} from 'src/components/lib/analytics-filter-card';
import TimeframeFilter from './timeframe-filter';

import css from './index.css';


type FilterType =
  | 'timeframe'
  | 'messagetype'
  | 'recipient'
  | 'relatedTo'
  | 'tracking_events'
  | 'more';

export type FilterProps = {
  activeTabs: string[];
  query: Record<any, any>;
  onQueryChange: (update: Record<any, any>, options?: UpdateOptions) => void;
  router: Router;
  location: RouteLocation;
  minDate?: string;
  defaultTimeAgo?: string;
  direction?: 'left' | 'right';
};

type OwnProps = {
  eventType?: ClientEventType;
  includeFilters?: FilterType[];
  extraFilters?: React.ComponentType<FilterProps>[];
  workflowEntityType?: string;
  entityType?: string;
  minDate?: string;
  showDefaultFilter?: boolean;
  className?: string;
  defaultTimeAgo?: string;
};

type Props = OwnProps & {
  location: RouteLocation;
  router: Router;
  eventTypeCounts: {
    [key: string]: number;
  };
  query: Record<any, any>;
  onQueryChange: (update: Record<any, any>, options?: UpdateOptions) => void;
  atsFieldValues: {
    [id: string]: {
      values: AtsAnalyticsField[];
    };
  };
  entityFilters: Array<EntityFilter>;
  showSettingsLink: string | null | undefined;
  defaultQuery: AnalyticsQuery | null | undefined;
  agentDefaultFiltersEnabled: boolean;
  className?: string;
};

// @ts-expect-error - TS7031 - Binding element 'entityType' implicitly has an 'any' type.
const mapStateToProps = (state: ReduxState, {entityType}): any => ({
  agentDefaultFiltersEnabled: state.agency.config.agentDefaultFiltersEnabled,
  showSettingsLink:
    (getCanEditSettings(state) && getCandidateFieldName(state)) || null,
  eventTypeCounts: state.eventTypeCounts.agency,
  atsFieldValues: getAtsFieldValues(state),
  entityFilters: entityType
    ? selectEntityFiltersForType(state, entityType)
    : selectEntityFilters(state),
  defaultQuery: state.report.defaultQuery,
});

const AnalyticsFilters = ({
  location,
  query,
  router: history,
  eventType,
  eventTypeCounts,

  includeFilters = [
    'timeframe',
    'recipient',
    // 'relatedTo',
    // 'tracking_events',
    'more',
  ],

  extraFilters = [],
  atsFieldValues,
  entityFilters,
  onQueryChange,
  showSettingsLink,
  defaultQuery,
  agentDefaultFiltersEnabled,
  minDate,
  showDefaultFilter = true,
  className,
  defaultTimeAgo = JSON.stringify({days: 90}),
}: Props) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const defaultState: {
    activeTabs: string[];
    openFilter: string | null | undefined;
    lastFilterAction: 'none' | 'cleared' | 'restored' | 'saved';
  } = {
    activeTabs: [],
    openFilter: null,
    lastFilterAction: 'none',
  };
  const [state, setState] = React.useState(defaultState);
  const {activeTabs} = state;

  const dispatch = useDispatch();

  const eventTypes = [
    {value: 'survey', label: t('Email_Surveys', 'Email Surveys')},
    {value: 'message', label: t('Email_Messages', 'Email Messages')},
    {value: 'sms_survey', label: t('SMS_Surveys', 'SMS Surveys')},
    {value: 'sms_message', label: t('SMS_Messages', 'SMS Messages')},
  ];

  // @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
  const localQueryChange = (...args) => {
    // @ts-expect-error - TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
    onQueryChange(...args);
    setState({...state, lastFilterAction: 'none'});
  };

  const handleOpenFilter = (name: string) => () => {
    setState({...state, openFilter: name});
  };

  const queryAsArray = (
    key: string,
    options: UpdateOptions = {entityDataFilter: undefined},
  ) => {
    const getKey = options.entityDataFilter ? ['edf', key] : key;
    return get(query, getKey, []);
  };

  const _handleKeyedArrays = (key: string, evt: React.SyntheticEvent<any>) => {
    const {name} = evt.currentTarget;
    const queryArray = queryAsArray(key);

    const queryArgs = queryArray.includes(name)
      ? // @ts-expect-error - TS7006 - Parameter 'type' implicitly has an 'any' type.
        queryArray.filter((type) => {
          if (name === 'survey') {
            return !['survey', 'beefree_email'].includes(type);
          }
          return type !== name;
        })
      : name === 'survey'
      ? [...queryArray, name, 'beefree_email']
      : [...queryArray, name];

    localQueryChange({
      [key]: {$set: queryArgs},
    });
  };

  const handleEventTypeChange = _handleKeyedArrays.bind(this, 'event_types');
  const handleRecipientTypeChange = _handleKeyedArrays.bind(this, 'recipients');
  const handleTrackingEventTypeChange = _handleKeyedArrays.bind(
    this,
    'tracking_events',
  );

  const handleValueChange = (key: string) => (value: unknown) => {
    localQueryChange(
      {
        [key]: {$set: value},
      },
      {entityDataFilter: true},
    );
  };

  const handleFromAddressChange = (vals: string[]) => {
    localQueryChange({
      ['from_address']: {$set: vals},
    });
  };

  const selectedEventTypes = queryAsArray('event_types');
  const selectedRecipientTypes = queryAsArray('recipients');
  const selectedTrackingEventTypes = queryAsArray('tracking_events');
  const selectedFromAddresses = queryAsArray('from_address');

  const messageTypeLabel =
    selectedEventTypes.length === 1
      ? eventTypes.filter((type) => type.value === selectedEventTypes[0])[0]
          .label
      : t('Message_Type', 'Message Type');

  const {
    client,
    recruiter,
    office,
    account_manager: accountManager,
    brand,
  } = atsFieldValues;

  const edfOption = {entityDataFilter: true} as const;

  const selectedClients = client
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(client.senseAttribute, edfOption)
    : [];
  const selectedOffices = office
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(office.senseAttribute, edfOption)
    : [];
  const selectedRecruiters = recruiter
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(recruiter.senseAttribute, edfOption)
    : [];
  const selectedAccountManagers = accountManager
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(accountManager.senseAttribute, edfOption)
    : [];
  const selectedBrands = brand
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(brand.senseAttribute, edfOption)
    : [];

  const showRelatedToFilter = shouldShowFilter(
    // @ts-expect-error - TS2345 - Argument of type '{ values: AtsAnalyticsField[]; }' is not assignable to parameter of type 'AtsAnalyticsField'.
    client,
    recruiter,
    office,
    accountManager,
    brand,
  );

  const entityFiltersTransformed = Object.keys(atsFieldValues).reduce<
    Array<any>
  >((transformedFilters, fieldValueId) => {
    const fieldValue = atsFieldValues[fieldValueId];
    const entityFilter = entityFilters.find(
      ({id: entityFilterId}) => fieldValueId === entityFilterId,
    );
    if (entityFilter && fieldValue) {
      const selected = queryAsArray(
        `${entityFilter.entity_type}/${entityFilter.attribute_name}`,
        edfOption,
      );
      transformedFilters.push({
        ...entityFilter,
        ...fieldValue,
        selected,
      });
    }
    return transformedFilters;
  }, []);

  const entityFiltersActiveCount = entityFiltersTransformed.reduce(
    (prev, {selected}) => {
      if (selected) {
        if (Array.isArray(selected)) {
          return prev + selected.length;
        }
        return prev + 1;
      }
      return prev;
    },
    0,
  );

  const moreFiltersCount =
    entityFiltersActiveCount + selectedFromAddresses.length;

  return (
    <>
      <div className={classify(css.container, className)}>
        {/* timeframe is always active until date-less queries happen? */}

        {includeFilters.includes('timeframe') && (
          <TimeframeFilter
            query={query}
            activeTabs={activeTabs}
            onQueryChange={localQueryChange}
            router={history}
            location={location}
            minDate={minDate}
            defaultTimeAgo={defaultTimeAgo}
          />
        )}

        {includeFilters.includes('messagetype') && (
          <ClickAway>
            {({isOpen, onOpen, triggerRef, boundaryRef}) => (
              // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div className={css.filter} ref={triggerRef}>
                <FilterButton
                  className={css.button}
                  active={
                    activeTabs.includes('messagetype') ||
                    selectedEventTypes.length > 0
                  }
                  activeFilters={
                    selectedEventTypes.length > 1
                      ? selectedEventTypes.length
                      : 0
                  }
                  onClick={() => {
                    handleOpenFilter('messagetype');
                    onOpen();
                  }}
                >
                  {messageTypeLabel}
                </FilterButton>
                {isOpen && (
                  <div className={css.filterCard} ref={boundaryRef}>
                    {/* @ts-expect-error - TS2739 - Type '{ onChange: (evt: SyntheticEvent<any, Event>) => void; selectedTypes: any; eventTypes: { value: string; label: string; }[]; eventTypeCounts: { [key: string]: number; }; }' is missing the following properties from type 'Props': onSave, onCancel, history, location */}
                    <MessageTypeCard
                      onChange={handleEventTypeChange}
                      selectedTypes={selectedEventTypes}
                      eventTypes={eventTypes}
                      eventTypeCounts={eventTypeCounts}
                    />
                  </div>
                )}
              </div>
            )}
          </ClickAway>
        )}

        {showRelatedToFilter && includeFilters.includes('relatedTo') && (
          <ClickAway>
            {({isOpen, onOpen, triggerRef, boundaryRef}) => (
              // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div className={css.filter} ref={triggerRef}>
                <FilterButton
                  className={css.button}
                  active={
                    activeTabs.includes('relatedTo') ||
                    selectedClients.length +
                      selectedRecruiters.length +
                      selectedBrands.length +
                      selectedAccountManagers.length +
                      selectedOffices.length >
                      0
                  }
                  activeFilters={
                    selectedClients.length +
                    selectedRecruiters.length +
                    selectedBrands.length +
                    selectedAccountManagers.length +
                    selectedOffices.length
                  }
                  onClick={() => {
                    handleOpenFilter('relatedTo');
                    onOpen();
                  }}
                >
                  {t('Related_To', 'Related To')}
                </FilterButton>
                {isOpen && (
                  <div className={css.filterCard} ref={boundaryRef}>
                    {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                    <RelatedToCard
                      history={history}
                      location={location}
                      client={client}
                      office={office}
                      recruiter={recruiter}
                      accountManager={accountManager}
                      brand={brand}
                      selectedClients={selectedClients}
                      selectedOffices={selectedOffices}
                      selectedRecruiters={selectedRecruiters}
                      selectedAccountManagers={selectedAccountManagers}
                      selectedBrands={selectedBrands}
                      onStringListChange={handleValueChange}
                    />
                  </div>
                )}
              </div>
            )}
          </ClickAway>
        )}

        {includeFilters.includes('recipient') && (
          <ClickAway>
            {({isOpen, onOpen, triggerRef, boundaryRef}) => (
              // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div className={css.filter} ref={triggerRef}>
                <FilterButton
                  className={css.button}
                  active={
                    activeTabs.includes('recipient') ||
                    selectedRecipientTypes.length > 0
                  }
                  activeFilters={selectedRecipientTypes.length}
                  onClick={() => {
                    handleOpenFilter('recipient');
                    onOpen();
                  }}
                >
                  {t('Recipient', 'Recipient')}
                </FilterButton>
                {isOpen && (
                  <div className={css.filterCard} ref={boundaryRef}>
                    <RecipientCard
                      onRecipientTypeChange={handleRecipientTypeChange}
                      selectedRecipientTypes={selectedRecipientTypes}
                    />
                  </div>
                )}
              </div>
            )}
          </ClickAway>
        )}

        {includeFilters.includes('tracking_events') && (
          <ClickAway>
            {({isOpen, onOpen, triggerRef, boundaryRef}) => (
              // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div className={css.filter} ref={triggerRef}>
                <FilterButton
                  className={css.button}
                  active={
                    activeTabs.includes('tracking_events') ||
                    selectedTrackingEventTypes.length > 0
                  }
                  activeFilters={selectedTrackingEventTypes.length}
                  onClick={() => {
                    handleOpenFilter('tracking_events');
                    onOpen();
                  }}
                >
                  {t('Marketing', 'Marketing')}
                </FilterButton>
                {isOpen && (
                  <div className={css.filterCard} ref={boundaryRef}>
                    <TrackingEventCard
                      onTrackingEventTypeChange={handleTrackingEventTypeChange}
                      trackingEventTypes={getTrackingEvents(
                        eventType ||
                          (query.event_types && query.event_types[0]),
                      )}
                      selectedTrackingEventTypes={selectedTrackingEventTypes}
                    />
                  </div>
                )}
              </div>
            )}
          </ClickAway>
        )}

        {includeFilters.includes('more') && (
          <ClickAway>
            {({isOpen, onOpen, triggerRef, boundaryRef}) => (
              // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              <div className={css.filter} ref={triggerRef}>
                <FilterButton
                  className={css.button}
                  active={activeTabs.includes('more') || moreFiltersCount > 0}
                  activeFilters={moreFiltersCount}
                  onClick={() => {
                    handleOpenFilter('more');
                    onOpen();
                  }}
                >
                  {t('More_Filters', 'More Filters')}
                </FilterButton>
                {isOpen && (
                  <div className={css.filterCard} ref={boundaryRef}>
                    <MoreFiltersCard
                      filters={entityFiltersTransformed}
                      onValueChange={handleValueChange}
                      showSettingsLink={showSettingsLink}
                      onChangeFromAddress={handleFromAddressChange}
                      filteredFromAddresses={selectedFromAddresses}
                    />
                  </div>
                )}
              </div>
            )}
          </ClickAway>
        )}

        {extraFilters.map((Filter) => (
          <Filter
            query={query}
            activeTabs={activeTabs}
            onQueryChange={localQueryChange}
            router={history}
            location={location}
          />
        ))}

        {state.lastFilterAction !== 'cleared' && Object.keys(query).length > 0 && (
          <button
            className={css.textButton}
            onClick={() => {
              onQueryChange({$set: {}});
              setState({...state, lastFilterAction: 'cleared'});
            }}
          >
            {t('clear_filters', 'clear filters')}
          </button>
        )}
        {state.lastFilterAction !== 'none' && (
          <button className={css.textButton} disabled>
            {`${t('filters', 'filters')} `}
            {state.lastFilterAction}
          </button>
        )}
        {showDefaultFilter &&
          agentDefaultFiltersEnabled &&
          !isEqual(defaultQuery, query) &&
          state.lastFilterAction !== 'restored' && (
            <>
              <button
                className={css.textButton}
                onClick={() => {
                  onQueryChange({
                    $merge: defaultQuery,
                  });
                  setState({...state, lastFilterAction: 'restored'});
                }}
              >
                {t('restore_saved_filters', 'restore saved filters')}
              </button>
              <button
                className={css.textButton}
                onClick={() => {
                  dispatch(saveDefaultAnalyticsQuery(query));
                  setState({...state, lastFilterAction: 'saved'});
                }}
              >
                {t('save_these_filters', 'save these filters')}
              </button>
            </>
          )}
      </div>
    </>
  );
};

export default flow(
  connect(mapStateToProps),
  withAnalyticsQuery,
)(AnalyticsFilters) as React.ComponentType<OwnProps>;

const humanTimeframeQuery = (query: any) => {
  if (query.time_ago !== undefined) {
    const timeAgo = JSON.parse(query.time_ago).days;
    if (timeAgo !== undefined) {
      return `Last ${timeAgo} ${formatPlural(timeAgo, 'day', 'days')}`;
    }
  }
  if (query.start_date && query.end_date) {
    return 'Custom Date Range';
  }
  if (!query.start_date && !query.end_date && !query.time_ago) {
    return 'Last 90 days';
  }
  return 'Timeframe';
};

const shouldShowFilter = (
  ...fieldValues: Array<AtsAnalyticsField | null | undefined>
): boolean =>
  fieldValues.reduce(
    (prev, curr) =>
      prev ||
      Boolean(curr && curr.values === null && curr.error && curr.message) ||
      Boolean(curr && Array.isArray(curr.values)) ||
      Boolean(curr && curr.min != null && curr.max != null),
    false,
  );
